import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectRouteCoordinates } from '../mapSlice';
import { calculateBearing, calculateDistance } from '../logic/NavigationHelpers';

/**
 * Generates a simulation script from route coordinates
 * Converts route points into stepwise GPS updates with timing
 */
const generateRouteSimulationScript = (routeCoordinates) => {
    if (!routeCoordinates || routeCoordinates.length < 2) {
        return '// No route available. Please create a route first.';
    }

    // Sample the route to avoid overwhelming number of points
    const sampleInterval = Math.max(1, Math.floor(routeCoordinates.length / 100)); // Max 100 steps
    const sampledPoints = routeCoordinates.filter((_, index) => index % sampleInterval === 0);

    // Always include the last point
    if (routeCoordinates[routeCoordinates.length - 1] !== sampledPoints[sampledPoints.length - 1]) {
        sampledPoints.push(routeCoordinates[routeCoordinates.length - 1]);
    }

    // Generate steps with bearing and timing
    const steps = [];
    const speedKmh = 50; // Simulated driving speed
    const speedMs = speedKmh / 3.6; // Convert to m/s

    for (let i = 0; i < sampledPoints.length; i++) {
        const point = sampledPoints[i];
        const nextPoint = sampledPoints[i + 1];

        let heading = 0;
        let delay = 1000; // Default 1 second

        if (nextPoint) {
            heading = calculateBearing(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
            const distance = calculateDistance(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
            delay = Math.round((distance / speedMs) * 1000); // Time in ms to cover distance
            delay = Math.max(500, Math.min(delay, 5000)); // Clamp between 0.5s and 5s
        }

        steps.push({
            lat: point.lat,
            lng: point.lng,
            heading: Math.round(heading),
            speed: speedMs,
            delay: delay
        });
    }

    // Generate the actual JavaScript code
    const stepsJson = JSON.stringify(steps, null, 2);

    return `// Route Simulation Data
// 1. Paste this into console to load the route
// 2. Run startSimulation() to begin

// Store route steps globally
window.routeSteps = ${stepsJson};

console.log('✅ Route loaded! Run startSimulation() to begin drive.');
console.log('   (If nothing happens, make sure Monkey Patch is installed)');
`;
};

/**
 * Component that generates route-specific simulation scripts
 */
const RouteScriptGenerator = () => {
    const routeCoordinates = useSelector(selectRouteCoordinates);
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const script = generateRouteSimulationScript(routeCoordinates);
        navigator.clipboard.writeText(script).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const hasRoute = routeCoordinates && routeCoordinates.length >= 2;

    // Direct execution handler
    const handleRun = () => {
        if (!hasRoute) return;

        const script = generateRouteSimulationScript(routeCoordinates);

        // 1. Parse the script to get the data (or just regenerate the data)
        const sampleInterval = Math.max(1, Math.floor(routeCoordinates.length / 100));
        const sampledPoints = routeCoordinates.filter((_, index) => index % sampleInterval === 0);
        if (routeCoordinates[routeCoordinates.length - 1] !== sampledPoints[sampledPoints.length - 1]) {
            sampledPoints.push(routeCoordinates[routeCoordinates.length - 1]);
        }

        const steps = [];
        const speedKmh = 50;
        const speedMs = speedKmh / 3.6;

        for (let i = 0; i < sampledPoints.length; i++) {
            const point = sampledPoints[i];
            const nextPoint = sampledPoints[i + 1];
            let heading = 0;
            let delay = 1000;

            if (nextPoint) {
                heading = calculateBearing(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
                const distance = calculateDistance(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
                delay = Math.round((distance / speedMs) * 1000);
                delay = Math.max(500, Math.min(delay, 5000));
            }
            steps.push({ lat: point.lat, lng: point.lng, heading: Math.round(heading), speed: speedMs, delay: delay });
        }

        // 2. Set globally
        window.routeSteps = steps;
        console.log('🚗 Route steps loaded into window.routeSteps (' + steps.length + ' steps)');
        if (steps.length > 0) {
            console.log('   Start Point:', steps[0].lat, steps[0].lng);
        }

        // 3. Try to start
        if (typeof window.startSimulation === 'function') {
            if (window.MONKEY_PATCH_VERSION !== 3) {
                alert('Outdated Monkey Patch detected! Please copy and paste the new Monkey Patch (v3) from the console.');
                return;
            }
            console.log('▶️ Triggering startSimulation()...');
            window.startSimulation();
        } else {
            console.warn('⚠️ window.startSimulation not found. Please paste the Monkey Patch first!');
            alert('Monkey Patch not active! Please copy and paste the Monkey Patch script into the console first.');
        }
    };

    const handleJumpToStart = () => {
        if (!hasRoute) return;
        const start = routeCoordinates[0];

        if (typeof window.updateGPS === 'function') {
            console.log(`📍 Jumping to start: ${start.lat}, ${start.lng}`);
            // Update GPS manually (lat, lng, heading=0, speed=0)
            window.updateGPS(start.lat, start.lng, 0, 0);
        } else {
            alert('Monkey Patch not active!');
        }
    };

    return (
        <div style={{ marginTop: '10px' }}>
            <div style={{ fontSize: '10px', marginBottom: '5px', color: '#666' }}>
                Step 2: Run Simulation
            </div>

            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                <button
                    onClick={handleCopy}
                    disabled={!hasRoute}
                    style={{
                        flex: 1,
                        padding: '8px',
                        cursor: hasRoute ? 'pointer' : 'not-allowed',
                        backgroundColor: !hasRoute ? '#ccc' : (copied ? '#4CAF50' : '#FF9800'),
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        minWidth: '80px'
                    }}
                >
                    {copied ? '✓ Copied!' : '📋 Copy Script'}
                </button>

                <button
                    onClick={handleJumpToStart}
                    disabled={!hasRoute}
                    style={{
                        flex: 1,
                        padding: '8px',
                        cursor: hasRoute ? 'pointer' : 'not-allowed',
                        backgroundColor: !hasRoute ? '#ccc' : '#9C27B0',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        minWidth: '80px'
                    }}
                    title="Set GPS to start without running"
                >
                    📍 Jump to Start
                </button>

                <button
                    onClick={handleRun}
                    disabled={!hasRoute}
                    style={{
                        flex: 1,
                        padding: '8px',
                        cursor: hasRoute ? 'pointer' : 'not-allowed',
                        backgroundColor: !hasRoute ? '#ccc' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        minWidth: '80px'
                    }}
                >
                    ▶️ Run Now
                </button>
            </div>

            <div style={{ fontSize: '9px', marginTop: '3px', color: '#999' }}>
                {hasRoute ? 'Run immediately or copy script to console' : 'Create a route first'}
            </div>
        </div>
    );
};

export default RouteScriptGenerator;
